// Port de players.go : extraction d'un flux jouable depuis une URL de lecteur.

import {
  EMBED_MIRROR_HOSTS,
  SIBNET_BASE,
  embedMirrors,
  embedReferer,
  normalizeEmbedHost,
  resolveRelative,
} from "./constants.ts";
import * as embed4me from "./embed4me.ts";
import { fetchText } from "./proxy.ts";
import { unpackJS } from "./unpack.ts";

/** Source vidéo résolue depuis un lecteur : soit un master HLS dont il reste à
 *  choisir la qualité, soit un fichier progressif à lire tel quel. */
export interface Playable {
  url: string;
  referer: string;
  isHLS: boolean;
}

interface EmbedResolver {
  hosts: string[];
  resolve: (embedUrl: string) => Promise<Playable[]>;
}

// vidmoly répartit ses fichiers sur plusieurs pools de CDN — `prx-`, `box-`,
// `gate-`… — et réattribue le nœud dynamiquement : le même embed change d'hôte
// d'une lecture à l'autre. Filtrer sur un préfixe rendait donc un épisode
// injouable les jours où il tombait sur un pool non listé, puis jouable à
// nouveau le lendemain. La page ne porte qu'une seule URL `.m3u8` : n'exiger
// que le nom de fichier suffit à l'identifier.
const MASTER_RE = /https:\/\/[^'" \t\r\n]+\/master\.m3u8[^'" \t\r\n]*/;

/** Extrait le master.m3u8 d'un lecteur de la famille vidmoly (vidmoly.*,
 *  voembed.*), utilisé aussi bien par anime-sama que par voiranime. Les miroirs
 *  tombent régulièrement : on essaie les autres si besoin. */
async function resolveVidmoly(embedUrl: string): Promise<Playable[]> {
  for (const candidate of embedMirrors(normalizeEmbedHost(embedUrl))) {
    const page = await fetchText(candidate, { referer: embedReferer(candidate) });
    if (page === "") continue;

    const match = MASTER_RE.exec(page);
    if (match) {
      return [{ url: match[0], referer: embedReferer(candidate), isHLS: true }];
    }
  }

  return [];
}

/** sibnet sert un MP4 progressif, référencé en relatif dans le player de la
 *  page shell.php. Le Referer est obligatoire : sans lui le CDN répond 403. */
const SIBNET_SRC_RE = /src\s*:\s*"(\/v\/[^"]+\.mp4)"/;

async function resolveSibnet(embedUrl: string): Promise<Playable[]> {
  const page = await fetchText(embedUrl, { referer: embedReferer(embedUrl) });
  if (page === "") return [];

  const match = SIBNET_SRC_RE.exec(page);
  if (!match) return [];

  return [{ url: SIBNET_BASE + match[1], referer: SIBNET_BASE + "/", isHLS: false }];
}

/** smoothpre embarque un jwplayer dans du JavaScript compressé. Une fois
 *  décompressé, la page expose plusieurs masters HLS ("hls2", "hls3"…),
 *  certains absolus et signés, d'autres relatifs à l'hôte du lecteur. */
const SMOOTHPRE_LINK_RE = /"hls\d+"\s*:\s*"([^"]+)"/g;

async function resolveSmoothpre(embedUrl: string): Promise<Playable[]> {
  const page = await fetchText(embedUrl, { referer: embedReferer(embedUrl) });
  if (page === "") return [];

  const source = unpackJS(page) || page;
  const referer = embedReferer(embedUrl);

  const absolute: Playable[] = [];
  const relative: Playable[] = [];

  for (const match of source.matchAll(SMOOTHPRE_LINK_RE)) {
    const link = match[1]!.replaceAll("\\/", "/");

    const playable: Playable = {
      url: resolveRelative(embedUrl, link),
      referer,
      isHLS: true,
    };

    // les liens absolus portent déjà leur signature : on les essaie d'abord
    if (link.startsWith("http")) absolute.push(playable);
    else relative.push(playable);
  }

  return [...absolute, ...relative];
}

/** sendvid expose sa source directement dans la page du lecteur, en HLS quand
 *  il est disponible et en MP4 progressif sinon.
 *
 *  La page annonce aussi le fichier dans ses balises `og:video`, plus haut que
 *  le lecteur : `https://sendvid.com/<id>.mp4`. Cette URL-là ne fait que
 *  rediriger vers la page elle-même. La prendre — c'était la première `.mp4`
 *  rencontrée — rendait injouable tout épisode dont sendvid était le seul
 *  lecteur encore en ligne. Le vrai fichier, signé et servi par
 *  `videosN.sendvid.com`, est celui qu'on déclare au lecteur. */
const SENDVID_HLS_RE = /https:\/\/[^"'\s\\]+\.m3u8[^"'\s\\]*/;
const SENDVID_MP4_RE =
  /(?:video_source\s*=\s*|<source\s+src=)"(https:\/\/[^"\s\\]+\.mp4[^"\s\\]*)"/;

export function sendvidPlayables(page: string, referer: string): Playable[] {
  const playables: Playable[] = [];

  const hls = SENDVID_HLS_RE.exec(page);
  if (hls) playables.push({ url: hls[0], referer, isHLS: true });

  const mp4 = SENDVID_MP4_RE.exec(page);
  if (mp4) playables.push({ url: mp4[1]!, referer, isHLS: false });

  return playables;
}

async function resolveSendvid(embedUrl: string): Promise<Playable[]> {
  const page = await fetchText(embedUrl, { referer: embedReferer(embedUrl) });
  if (page === "") return [];

  return sendvidPlayables(page, embedReferer(embedUrl));
}

/** embed4me ne met rien dans sa page : il faut interroger son API, dont la
 *  réponse est chiffrée. Voir `embed4me.ts` pour le détail.
 *
 *  C'est le seul lecteur d'anime-sama à proposer du 1080p ; il est donc essayé
 *  en premier. Deux flux en sortent : celui du CDN d'origine, puis le même
 *  relayé par le domaine du lecteur, qui sert de repli. */
async function resolveEmbed4me(embedUrl: string): Promise<Playable[]> {
  const id = embed4me.videoId(embedUrl);
  if (!id) return [];

  const referer = embedReferer(embedUrl);

  const body = await fetchText(embed4me.apiUrl(embedUrl, id), { referer });
  if (body === "") return [];

  const video = await embed4me.decryptVideo(body);
  if (!video) return [];

  const playables: Playable[] = [];

  for (const field of [video.source, video.cfNative]) {
    if (typeof field === "string" && field.startsWith("http")) {
      playables.push({ url: field, referer, isHLS: true });
    }
  }

  return playables;
}

/** vk publie le master HLS dans le JSON du lecteur, slashs échappés. */
const VK_HLS_RE = /"hls"\s*:\s*"(https:[^"]+)"/;

async function resolveVK(embedUrl: string): Promise<Playable[]> {
  const page = await fetchText(embedUrl, { referer: embedReferer(embedUrl) });
  if (page === "") return [];

  const match = VK_HLS_RE.exec(page);
  if (!match) return [];

  return [
    {
      url: match[1]!.replaceAll("\\/", "/"),
      referer: "https://vk.com/",
      isHLS: true,
    },
  ];
}

/** Lecteurs dont on sait extraire un flux, par ordre de préférence. embed4me
 *  passe devant parce qu'il est le seul à servir du 1080p ; la famille vidmoly
 *  (servie par anime-sama sous ansembed.net) reste la plus fiable et prend le
 *  relais dès qu'embed4me ne rend rien. Les autres servent de repli quand un
 *  épisode n'est disponible sur aucun des deux.
 *
 *  Intervertir les deux premières lignes suffit à repasser sur vidmoly par
 *  défaut, par exemple si l'opérateur d'embed4me change sa clé. */
const EMBED_RESOLVERS: EmbedResolver[] = [
  { hosts: ["embed4me."], resolve: resolveEmbed4me },
  { hosts: ["ansembed.", "voembed.", "vidmoly."], resolve: resolveVidmoly },
  { hosts: ["video.sibnet.ru"], resolve: resolveSibnet },
  { hosts: ["smoothpre."], resolve: resolveSmoothpre },
  { hosts: ["sendvid."], resolve: resolveSendvid },
  { hosts: ["vk.com", "vkvideo.ru"], resolve: resolveVK },
];

/** La comparaison est insensible à la casse : anime-sama écrit par exemple
 *  "https://Smoothpre.com". */
function resolverIndexFor(embedUrl: string): number {
  const lower = embedUrl.toLowerCase();

  return EMBED_RESOLVERS.findIndex((resolver) =>
    resolver.hosts.some((host) => lower.includes(host)),
  );
}

export function isSupportedEmbed(embedUrl: string): boolean {
  return resolverIndexFor(embedUrl) !== -1;
}

/** Classe une URL de lecteur selon l'ordre de EMBED_RESOLVERS. Les lecteurs
 *  inconnus passent en dernier. */
export function embedPriority(embedUrl: string): number {
  const index = resolverIndexFor(embedUrl);

  return index === -1 ? EMBED_RESOLVERS.length : index;
}

/** Nom lisible du lecteur, pour l'affichage. */
export function embedLabel(embedUrl: string): string {
  try {
    const host = new URL(embedUrl).hostname.toLowerCase().replace(/^www\./, "");

    if (EMBED_MIRROR_HOSTS.includes(host)) return "vidmoly";

    return host.split(".").slice(-2, -1)[0] ?? host;
  } catch {
    return "lecteur";
  }
}

export async function resolvePlayables(embedUrl: string): Promise<Playable[]> {
  const index = resolverIndexFor(embedUrl);
  if (index === -1) return [];

  try {
    return await EMBED_RESOLVERS[index]!.resolve(embedUrl);
  } catch {
    return [];
  }
}
